import { lazy, Suspense } from 'react';
import { Routes, Route, Outlet } from 'react-router';
import { ConvexClientProvider } from './components/ConvexClientProvider';
import { ThemeProvider } from './components/theme-provider';
import { TimeFormatProvider } from './lib/time-format-context';
import { NativeTabsProvider } from './lib/native-tabs-context';
import { AppLayout } from './components/AppLayout';
import { ShareLayout } from './components/ShareLayout';
import { Toaster } from './components/ui/toaster';
import { Loader2 } from 'lucide-react';

// Eager route imports (core app functionality)
import Index from './routes/index';
import SignIn from './routes/signin';
import Blocks from './routes/blocks/index';
import BlockNew from './routes/blocks/new';
import BlockDetail from './routes/blocks/$id';
import BlockEdit from './routes/blocks/$id.edit';
import Daily from './routes/daily';
import Settings from './routes/settings';
import Admin from './routes/admin';
import ShareBlock from './routes/share/$id';
import Privacy from './routes/privacy';
import Terms from './routes/terms';
import EmailInbox from './routes/email/index';
import ComposeEmail from './routes/email/compose';
import GoogleOAuthCallback from './routes/oauth/google/callback';
import ExpenseDetail from './routes/expenses/$id';
import Expenses from './routes/expenses/index';

// Lazy route imports (heavy dependencies - recharts, leaflet)
const Weekly = lazy(() => import('./routes/weekly'));
const Monthly = lazy(() => import('./routes/monthly'));
const Codes = lazy(() => import('./routes/codes'));
const Insights = lazy(() => import('./routes/insights'));

// Loading fallback for lazy routes
function RouteLoader() {
  return (
    <div className="flex h-[50vh] items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
    </div>
  );
}

function RootLayout() {
  return (
    <ConvexClientProvider>
      <ThemeProvider defaultTheme="system" storageKey="flextrack-theme">
        <TimeFormatProvider>
          <NativeTabsProvider>
            <Outlet />
            <Toaster />
          </NativeTabsProvider>
        </TimeFormatProvider>
      </ThemeProvider>
    </ConvexClientProvider>
  );
}

export default function App() {
  return (
    <Routes>
      <Route element={<RootLayout />}>
        {/* Main app layout */}
        <Route element={<AppLayout />}>
          <Route index element={<Index />} />
          <Route path="signin" element={<SignIn />} />
          <Route path="blocks" element={<Blocks />} />
          <Route path="blocks/new" element={<BlockNew />} />
          <Route path="blocks/:id" element={<BlockDetail />} />
          <Route path="blocks/:id/edit" element={<BlockEdit />} />
          <Route path="expenses" element={<Expenses />} />
          <Route path="expenses/:id" element={<ExpenseDetail />} />
          <Route path="daily" element={<Daily />} />
          <Route path="weekly" element={<Suspense fallback={<RouteLoader />}><Weekly /></Suspense>} />
          <Route path="monthly" element={<Suspense fallback={<RouteLoader />}><Monthly /></Suspense>} />
          <Route path="insights" element={<Suspense fallback={<RouteLoader />}><Insights /></Suspense>} />
          <Route path="settings" element={<Settings />} />
          <Route path="privacy" element={<Privacy />} />
          <Route path="terms" element={<Terms />} />
          <Route path="codes" element={<Suspense fallback={<RouteLoader />}><Codes /></Suspense>} />
          <Route path="admin" element={<Admin />} />
          <Route path="email" element={<EmailInbox />} />
          <Route path="email/compose" element={<ComposeEmail />} />
        </Route>

        {/* Share layout (no nav) */}
        <Route element={<ShareLayout />}>
          <Route path="share/:id" element={<ShareBlock />} />
        </Route>

        {/* OAuth callback (standalone, no layout) */}
        <Route path="oauth/google/callback" element={<GoogleOAuthCallback />} />
      </Route>
    </Routes>
  );
}
